import React, { useEffect, useRef, useState } from 'react';
import { Alert, alpha, Box, Button, FormControlLabel, Radio, RadioGroup, Stack, TextField, Theme } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import { SlicerAnswerOutcome, SlicerPromotionItem, SlicerPromotionQuestion } from 'api/element-issues';
import useBundleTranslation from 'i18n';
import {
    buildRetryAnswer,
    buildSlicerItemAnswer,
    canSubmitItem,
    classifyItemResult,
    draftForSelection,
    EMPTY_DRAFT,
    hiddenMemberCount,
    pickItemResult,
    reasonRequired,
    SlicerItemDraft,
    submissionOutcome,
} from './slicer-promotion';

interface Props {
    question: SlicerPromotionQuestion;
    isSubmitting: boolean;
    /**
     * Posts one item's structured answer JSON through the ordinary answer
     * path and resolves with the per-item results plus the reconciliation
     * status (the hook re-fetches the projection before resolving); rejects
     * only on transport/authorization failures.
     */
    onSubmit: (answer: string) => Promise<SlicerAnswerOutcome | void>;
}

type ItemNoticeKind = 'conflict' | 'changed' | 'rejected' | 'unconfirmed' | 'pending' | 'failed';

interface ItemNotice {
    kind: ItemNoticeKind;
    /** Steward-safe message built server-side; the i18n fallback shows when null. */
    message: string | null;
}

const NOTICE_DISPLAY: Record<ItemNoticeKind, { tag: string; fallbackKey: string; warning: boolean }> = {
    conflict: { tag: 'stale', fallbackKey: 'clarifications_slicer_stale', warning: false },
    changed: { tag: 'changed', fallbackKey: 'clarifications_slicer_stale', warning: false },
    rejected: { tag: 'rejected', fallbackKey: 'clarifications_slicer_submit_failed', warning: false },
    unconfirmed: { tag: 'unconfirmed', fallbackKey: 'clarifications_slicer_unconfirmed', warning: true },
    pending: { tag: 'pending_note', fallbackKey: 'clarifications_slicer_pending', warning: true },
    failed: { tag: 'failed', fallbackKey: 'clarifications_slicer_submit_failed', warning: false },
};

/**
 * Structured control for the grouped `slicer_promotion` governance question
 * (promotion spec 4.11): one card per field with the offered choices, a
 * mandatory reason when the picked choice requires one, per-item submit
 * (answers apply independently under a per-item CAS token), the
 * activation-blocked notice, a retry path for half-completed activations, and
 * a collapsed section listing deferred (*Not sure*) decisions.
 *
 * There is no skip affordance: deferral IS the *Not sure* answer, and the
 * backend ignores skip for this family.
 */
export default function SlicerPromotionControl({ question, isSubmitting, onSubmit }: Props) {
    const { t } = useBundleTranslation('app/extreport/concierge');

    // The typed view is a cast off `type` alone, so both arrays are only as
    // present as the payload makes them — a missing one must not throw inside
    // the carousel that every external report page renders.
    const items = question.items ?? [];
    const deferredItems = question.deferredItems ?? [];

    const [drafts, setDrafts] = useState<Record<string, SlicerItemDraft>>({});
    // Notices are keyed by slicerUid, not itemKey: the activation saga can
    // re-derive an item's key between submit and refetch (kind-flip), and the
    // notice belongs to the slicer either way.
    const [notices, setNotices] = useState<Record<string, ItemNotice>>({});
    const [submittingKey, setSubmittingKey] = useState<string | null>(null);
    const [deferredOpen, setDeferredOpen] = useState(false);

    // The refreshed projection arrives through props while a submit handler is
    // still awaiting; the ref lets the handler classify the outcome against
    // the CURRENT items instead of its stale closure.
    const latestItemsRef = useRef<SlicerPromotionItem[]>(items);
    latestItemsRef.current = items;

    // Reset per question; a refetch of the SAME question keeps in-progress drafts.
    useEffect(() => {
        setDrafts({});
        setNotices({});
        setDeferredOpen(false);
    }, [question.questionId]);

    const draftFor = (item: SlicerPromotionItem): SlicerItemDraft => drafts[item.itemKey] ?? EMPTY_DRAFT;

    const clearNotice = (slicerUid: string) => {
        setNotices((prev) => {
            if (!(slicerUid in prev)) return prev;
            const next = { ...prev };
            delete next[slicerUid];
            return next;
        });
    };

    const patchDraft = (item: SlicerPromotionItem, patch: Partial<SlicerItemDraft>) => {
        if (isSubmitting) return;
        setDrafts((prev) => ({ ...prev, [item.itemKey]: { ...EMPTY_DRAFT, ...prev[item.itemKey], ...patch } }));
        clearNotice(item.slicerUid);
    };

    // Option changes go through draftForSelection so a reason typed for a
    // reason-requiring option never survives a switch to one that isn't.
    const selectOption = (item: SlicerPromotionItem, value: string) => {
        if (isSubmitting) return;
        setDrafts((prev) => ({
            ...prev,
            [item.itemKey]: draftForSelection(item, prev[item.itemKey] ?? EMPTY_DRAFT, value),
        }));
        clearNotice(item.slicerUid);
    };

    const clearDraft = (itemKey: string) => {
        setDrafts((prev) => {
            const next = { ...prev };
            delete next[itemKey];
            return next;
        });
    };

    const submitAnswer = async (item: SlicerPromotionItem, answer: string) => {
        if (isSubmitting) return;
        clearNotice(item.slicerUid);
        setSubmittingKey(item.itemKey);
        try {
            const response = await onSubmit(answer);
            const result = response ? pickItemResult(response.results, item.itemKey) : null;
            if (result) {
                // Primary signal: the backend's per-item result (echoed under
                // the SUBMITTED key, immune to a kind-flip in the refetched
                // projection).
                const outcome = classifyItemResult(result);
                if (outcome.kind === 'applied') {
                    clearDraft(item.itemKey);
                } else if (outcome.kind === 'pending') {
                    // Surface the backend's detail when it sent one; the
                    // refreshed item's pending state carries the retry.
                    if (outcome.message !== null) {
                        setNotices((prev) => ({
                            ...prev,
                            [item.slicerUid]: { kind: 'pending', message: outcome.message },
                        }));
                    }
                } else {
                    setNotices((prev) => ({
                        ...prev,
                        [item.slicerUid]: {
                            kind: outcome.kind === 'changed' ? 'changed' : 'rejected',
                            message: outcome.message,
                        },
                    }));
                }
            } else if (response && !response.reconciled) {
                // No readable result AND the reconciliation refetch failed:
                // the outcome is simply unknown — never claim a stale conflict.
                setNotices((prev) => ({ ...prev, [item.slicerUid]: { kind: 'unconfirmed', message: null } }));
            } else {
                // Fallback: classify against the refetched projection.
                const outcome = submissionOutcome(item, latestItemsRef.current);
                if (outcome === 'conflict') {
                    setNotices((prev) => ({ ...prev, [item.slicerUid]: { kind: 'conflict', message: null } }));
                } else if (outcome === 'applied') {
                    clearDraft(item.itemKey);
                }
                // 'pending': the item re-renders in its activation_pending
                // state with the retry affordance; nothing to record locally.
            }
        } catch {
            setNotices((prev) => ({ ...prev, [item.slicerUid]: { kind: 'failed', message: null } }));
        } finally {
            setSubmittingKey(null);
        }
    };

    const handleSubmit = (item: SlicerPromotionItem) => {
        const draft = draftFor(item);
        if (!canSubmitItem(item, draft)) return;
        void submitAnswer(item, buildSlicerItemAnswer(item, draft));
    };

    const handleRetry = (item: SlicerPromotionItem) => {
        void submitAnswer(item, buildRetryAnswer(item));
    };

    // An applied, stale or unknown item leaves the refreshed projection, taking
    // its card — and any notice rendered inside it — with it. Those notices are
    // the only feedback the steward gets for that submission, so they are
    // rendered at group level instead of vanishing with the card.
    const presentSlicerUids = new Set(items.map((item) => item.slicerUid));
    const departedNotices = Object.entries(notices).filter(([slicerUid]) => !presentSlicerUids.has(slicerUid));

    return (
        <Stack data-test="clarifications_slicer_control">
            {0 === items.length && (
                <Box sx={{ pt: 1.5 }}>
                    <Alert severity={'info'} variant={'standard'}>
                        {t('clarifications_slicer_none_open')}
                    </Alert>
                </Box>
            )}
            {items.length > 0 && (
                <Stack sx={{ py: 1.5 }} spacing={1}>
                    {items.map((item) => {
                        const draft = draftFor(item);
                        const pending = item.state === 'activation_pending';
                        const busy = isSubmitting || submittingKey !== null;
                        const error = notices[item.slicerUid];
                        const hidden = hiddenMemberCount(item);
                        return (
                            <Stack
                                key={item.itemKey}
                                spacing={1}
                                data-test={`clarifications_slicer_item_${item.itemKey}`}
                                sx={{
                                    p: 1,
                                    border: '1px solid',
                                    borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
                                    borderRadius: '3px',
                                }}
                            >
                                <Box>
                                    <Box sx={{ fontWeight: 600 }}>{item.fieldCaption}</Box>
                                    <Box>{item.question}</Box>
                                </Box>
                                {item.samples.length > 0 && (
                                    <Box sx={{ color: 'text.secondary' }}>
                                        {t('clarifications_slicer_values')}: {item.samples.join(', ')}
                                        {hidden > 0 && (
                                            <> {t('clarifications_slicer_more_values', { count: hidden })}</>
                                        )}
                                    </Box>
                                )}
                                {'' !== item.consequence && (
                                    <Box
                                        sx={{
                                            color: 'text.secondary',
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        {item.consequence}
                                    </Box>
                                )}
                                {item.activationBlocked && '' !== item.activationBlockedNotice && (
                                    <Alert
                                        variant={'accent'}
                                        severity={'warning'}
                                        data-test={`clarifications_slicer_blocked_${item.itemKey}`}
                                        icon={<IconMi icon={'alert'} />}
                                    >
                                        {item.activationBlockedNotice}
                                    </Alert>
                                )}

                                {pending ? (
                                    <>
                                        <Alert
                                            variant={'accent'}
                                            severity={'warning'}
                                            data-test={`clarifications_slicer_pending_${item.itemKey}`}
                                            icon={<IconMi icon={'alert'} />}
                                        >
                                            {t('clarifications_slicer_pending')}
                                        </Alert>
                                        <Box>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                disabled={busy}
                                                onClick={() => handleRetry(item)}
                                                data-test={`clarifications_slicer_retry_${item.itemKey}`}
                                            >
                                                {t('clarifications_slicer_retry')}
                                            </Button>
                                        </Box>
                                    </>
                                ) : (
                                    <>
                                        <RadioGroup
                                            name="radio-buttons-group"
                                            value={draft.answer}
                                            onChange={(event, value) => {
                                                selectOption(item, value);
                                            }}
                                            aria-label={item.question}
                                        >
                                            {item.options.map((option) => {
                                                return (
                                                    <FormControlLabel
                                                        value={option.value}
                                                        control={<Radio />}
                                                        label={option.label}
                                                        disabled={busy}
                                                        data-test={`clarifications_slicer_option_${item.itemKey}_${option.value}`}
                                                    />
                                                );
                                            })}
                                        </RadioGroup>

                                        {reasonRequired(item, draft) && (
                                            <Box>
                                                <TextField
                                                    fullWidth={true}
                                                    placeholder={t('clarifications_slicer_reason_ph')}
                                                    aria-label={t('clarifications_slicer_reason_label')}
                                                    value={draft.reason}
                                                    disabled={busy}
                                                    onChange={(event) => {
                                                        patchDraft(item, { reason: event.target.value });
                                                    }}
                                                    data-test={`clarifications_slicer_reason_${item.itemKey}`}
                                                    rows={2}
                                                    multiline
                                                />
                                                {'' === draft.reason.trim() && (
                                                    <Box sx={{ mt: 0.25, fontSize: '11px', color: 'text.secondary' }}>
                                                        {t('clarifications_slicer_reason_required')}
                                                    </Box>
                                                )}
                                            </Box>
                                        )}

                                        <Box>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                disabled={busy || !canSubmitItem(item, draft)}
                                                onClick={() => handleSubmit(item)}
                                                data-test={`clarifications_slicer_submit_${item.itemKey}`}
                                            >
                                                {t('clarifications_slicer_submit')}
                                            </Button>
                                        </Box>
                                    </>
                                )}

                                {error && (
                                    <Alert
                                        severity={NOTICE_DISPLAY[error.kind].warning ? 'warning' : 'error'}
                                        variant="standard"
                                        data-test={`clarifications_slicer_${NOTICE_DISPLAY[error.kind].tag}_${item.itemKey}`}
                                    >
                                        {error.message ?? t(NOTICE_DISPLAY[error.kind].fallbackKey)}
                                    </Alert>
                                )}
                            </Stack>
                        );
                    })}
                </Stack>
            )}
            {departedNotices.length > 0 && (
                <Stack spacing={1} sx={{ pb: 1.5 }}>
                    {departedNotices.map(([slicerUid, notice]) => (
                        <Alert
                            severity={NOTICE_DISPLAY[notice.kind].warning ? 'warning' : 'error'}
                            variant="standard"
                            key={slicerUid}
                            data-test={`clarifications_slicer_${NOTICE_DISPLAY[notice.kind].tag}_slicer_${slicerUid}`}
                        >
                            {notice.message ?? t(NOTICE_DISPLAY[notice.kind].fallbackKey)}
                        </Alert>
                    ))}
                </Stack>
            )}

            {deferredItems.length > 0 && (
                <Box
                    sx={{
                        mx: '-12px',
                        px: 1.5,
                        borderTop: '1px solid',
                        borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
                        pt: 1.5,
                    }}
                >
                    <Button
                        variant="text"
                        onClick={() => setDeferredOpen((v) => !v)}
                        aria-expanded={deferredOpen}
                        data-test="clarifications_slicer_deferred_toggle"
                        endIcon={<IconMi icon={deferredOpen ? 'chevron-up' : 'chevron-down'} />}
                        sx={{}}
                    >
                        {t('clarifications_slicer_deferred_title', { count: deferredItems.length })}
                    </Button>
                    {deferredOpen && (
                        <Box
                            sx={{
                                border: '1px solid',
                                borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
                                borderRadius: '3px',
                            }}
                        >
                            {deferredItems.map((deferred, index) => (
                                <Stack
                                    key={`${index}|${deferred.fieldCaption}|${deferred.deferredTime}`}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    spacing={1}
                                    data-test={`clarifications_slicer_deferred_row_${index}`}
                                    sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        borderTop: index == 0 ? undefined : '1px solid',
                                        borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
                                    }}
                                >
                                    <Box>{deferred.fieldCaption}</Box>
                                    <Box sx={{ fontSize: '11px', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                        {deferred.deferredTime}
                                    </Box>
                                </Stack>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
        </Stack>
    );
}
